import React from 'react';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import { getTranslation } from '../../../i18n';
import type { SupportedLanguage, DuplicateDialogData } from '../../../types';

interface DuplicateDialogProps {
  data: DuplicateDialogData;
  language: SupportedLanguage;
  onViewExisting: () => void;
  onReanalyze: () => void;
  onCancel: () => void;
}

const DuplicateDialog: React.FC<DuplicateDialogProps> = ({ data, language, onViewExisting, onReanalyze, onCancel }) => {
  const t = (key: string) => getTranslation(language, key);

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center">
      <div className="fixed inset-0 bg-black/80" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-md mx-4 bg-gradient-to-br from-slate-900 to-slate-800 border border-default rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-default">
          <div className="p-2.5 bg-amber-500/20 rounded-xl">
            <AlertCircle size={22} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-content">{t('duplicateDetected') || 'Duplicate Document'}</h3>
            <p className="text-xs text-slate-400">{t('duplicateMessage') || 'This document has already been analyzed'}</p>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-6 py-5">
          <p className="text-sm text-slate-300 mb-4">
            {t('duplicateExplanation') || 'You already have an analysis for:'}
          </p>
          <div className="p-4 rounded-xl bg-surface-secondary border border-subtle mb-4">
            <p className="text-content font-semibold">{data.companyDisplay}</p>
            {data.createdAt && (
              <p className="text-xs text-slate-400 mt-1">
                {t('analyzedOn') || 'Analyzed on'}: {new Date(data.createdAt).toLocaleDateString()}
              </p>
            )}
          </div>
          <p className="text-xs text-slate-400">
            {t('duplicateChoiceHint') || 'Would you like to view the existing analysis or create a new one?'}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-900/50 border-t border-default">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-400 hover:text-content transition-colors"
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            onClick={onReanalyze}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-content text-sm font-medium rounded-lg transition-colors"
          >
            {t('analyzeAgain') || 'Analyze Again'}
          </button>
          <button
            onClick={onViewExisting}
            className="px-4 py-2 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-content-inverse text-sm font-medium rounded-lg transition-colors shadow-lg shadow-violet-500/20"
          >
            {t('viewExisting') || 'View Existing'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DuplicateDialog);
